import { useAxiosClient } from 'app/data/api'

import { Project } from '../types/tasks'

interface ProjectsResponse {
  items: Project[]
  total: number
  current_page?: string
  current_page_backwards?: string
  previous_page?: string
  next_page?: string
}

// Custom hook to interact with the projects API
export const useProjectsApi = () => {
  const axiosClient = useAxiosClient()

  /**
   * Fetch projects with optional filtering and cursor-based pagination
   * @param cursor - The cursor for pagination (optional)
   * @param size - The number of projects per page (default: 20)
   * @param includeDeleted - Whether to include deleted projects (optional)
   * @returns A promise that resolves to a ProjectsResponse object
   */
  const getProjects = async (
    cursor?: string,
    size: number = 20,
    status: string | null = null,
    includeDeleted: boolean = false
  ): Promise<ProjectsResponse> => {
    const params = new URLSearchParams({
      ...(cursor && { cursor }),
      size: size.toString(),
      ...(status && { status }),
      ...(includeDeleted && { include_deleted: 'true' }),
    })

    console.log('getProjects: Fetching projects with params:', params.toString())

    try {
      const { data } = await axiosClient.get<ProjectsResponse>(`/projects/?${params}`)
      //console.log(`getProjects: Fetched ${data.items.length} projects`)
      return data
    } catch (error) {
      console.error('Error fetching projects:', error)
      throw error
    }
  }

  // Create a new project
  const createProject = async (projectData: Omit<Project, 'id'>): Promise<Project> => {
    console.log('createProject: Creating new project', projectData)
    try {
      const { data } = await axiosClient.post<Project>('/projects/', projectData)
      console.log('createProject: Project created successfully', data)
      return data
    } catch (error) {
      console.error('Error creating project:', error)
      throw error
    }
  }

  // Update an existing project
  const updateProject = async (id: string, projectData: Partial<Project>): Promise<Project> => {
    console.log(`updateProject: Updating project ${id}`, projectData)
    try {
      const { data } = await axiosClient.put<Project>(`/projects/${id}`, projectData)
      console.log('updateProject: Project updated successfully', data)
      return data
    } catch (error) {
      console.error('Error updating project:', error)
      throw error
    }
  }

  // Delete a project
  const deleteProject = async (id: string): Promise<void> => {
    console.log(`deleteProject: Deleting project ${id}`)
    try {
      await axiosClient.delete(`/projects/${id}`)
      console.log(`deleteProject: Project ${id} deleted successfully`)
    } catch (error) {
      console.error('Error deleting project:', error)
      throw error
    }
  }

  // Undelete a project
  const undeleteProject = async (id: string): Promise<void> => {
    console.log(`undeleteProject: Undeleting project ${id}`)
    try {
      await axiosClient.post(`/projects/${id}/restore`)
      console.log(`undeleteProject: Project ${id} undeleted successfully`)
    } catch (error) {
      console.error('Error undeleting project:', error)
      throw error
    }
  }

  // Return an object with all the API functions
  return { getProjects, createProject, updateProject, deleteProject, undeleteProject }
}

// Add this line at the end of the file
export type { ProjectsResponse }
